import React from 'react'
import styled from 'styled-components'
import { Icon } from '../../atoms/Icon'
import { highlightMatch } from '../../../utils/highlightMatch'

const ResultsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.small} 0;
`

const SectionHeader = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.large};
  margin-top: ${({ theme }) => theme.spacing.small};

  &:first-child {
    margin-top: 0;
  }
`

const ResultItem = styled.div<{ $highlighted: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.large};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  background: ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.background.secondary : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`

const ResultIcon = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 12px;
`

const ResultCount = styled.div`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.large};
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-top: ${({ theme }) => theme.spacing.small};
`

interface GroupedResult {
  sectionTitle: string
  sectionTitleKey: string
  fields: Array<{
    label: string
    labelKey: string
  }>
}

interface FilterSearchResultsProps {
  results: GroupedResult[]
  totalCount: number
  searchQuery: string
  highlightedIndex: number
  onResultClick: (sectionTitleKey: string, fieldLabel: string) => void
  emptyMessage?: string
  countLabel?: (count: number) => string
}

export const FilterSearchResults: React.FC<FilterSearchResultsProps> = ({
  results,
  totalCount,
  searchQuery,
  highlightedIndex,
  onResultClick,
  emptyMessage = 'No matching filters',
  countLabel = (count) => `${count} result${count !== 1 ? 's' : ''}`,
}) => {
  if (results.length === 0) {
    return (
      <ResultsContainer>
        <EmptyState>{emptyMessage}</EmptyState>
      </ResultsContainer>
    )
  }

  let flatIndex = 0

  return (
    <ResultsContainer>
      {results.map((group) => (
        <React.Fragment key={group.sectionTitleKey}>
          <SectionHeader>{group.sectionTitle}</SectionHeader>
          {group.fields.map((field) => {
            const currentIndex = flatIndex++

            return (
              <ResultItem
                key={`${group.sectionTitleKey}-${field.labelKey}`}
                $highlighted={currentIndex === highlightedIndex}
                onClick={() =>
                  onResultClick(group.sectionTitleKey, field.label)
                }
              >
                <ResultIcon>
                  <Icon name="arrow-right" size="xs" />
                </ResultIcon>
                <span>{highlightMatch(field.label, searchQuery)}</span>
              </ResultItem>
            )
          })}
        </React.Fragment>
      ))}
      <ResultCount>{countLabel(totalCount)}</ResultCount>
    </ResultsContainer>
  )
}
